import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  FormLabel,
  Input,
  Link,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadApiBlob } from "../../lib/api";
import { useUpdateMedia } from "../../hooks/useMarketing";
import {
  bulkMedia,
  mediaError,
  tagNames,
  type MediaFile,
  type MediaUsage,
} from "./mediaApi";

export function MediaPreview({
  media,
  controls = false,
}: {
  media: MediaFile;
  controls?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [media.file]);
  if (failed)
    return (
      <Alert color="warning">
        Preview unavailable. Download the original to view it in a compatible
        application.
      </Alert>
    );
  if (media.file_type === "video")
    return (
      <Box
        component="video"
        key={media.file}
        src={media.file}
        poster={media.thumbnail || undefined}
        controls={controls}
        preload="metadata"
        muted={!controls}
        playsInline
        onError={() => setFailed(true)}
        sx={{
          width: "100%",
          maxHeight: controls ? 400 : 180,
          bgcolor: "neutral.900",
          borderRadius: "sm",
        }}
      />
    );
  if (media.file_type !== "image")
    return (
      <Typography level="body-sm">
        Preview unavailable for this file type.
      </Typography>
    );
  return (
    <Box
      component="img"
      src={controls ? media.file : media.thumbnail || media.file}
      alt={media.alt_text || media.name}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{
        width: "100%",
        height: controls ? "auto" : 180,
        maxHeight: controls ? 400 : undefined,
        objectFit: "contain",
        bgcolor: "background.level1",
        borderRadius: "sm",
      }}
    />
  );
}
export function MediaDetails({
  media,
  onClose,
  onUpdated,
}: {
  media: MediaFile;
  onClose: () => void;
  onUpdated: () => void;
}) {
  // Mounted with a source ID key: tag catalog refetches never reset this draft.
  const [name, setName] = useState(media.name);
  const [description, setDescription] = useState(media.description || "");
  const [alt, setAlt] = useState(media.alt_text || "");
  const [tags, setTags] = useState((media.tags || []).join(", "));
  const [baseline, setBaseline] = useState(
    JSON.stringify([name, description, alt, tags]),
  );
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const update = useUpdateMedia();
  const cache = useQueryClient();
  const production = media.source === "production";
  const dirty = baseline !== JSON.stringify([name, description, alt, tags]);
  const usage = useQuery({
    queryKey: ["media-usage", media.id],
    queryFn: async () =>
      (
        await api.get<MediaUsage>(
          `/api/marketing/combined-media/${media.id}/usage/`,
        )
      ).data,
  });
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const close = () => {
    if (
      !busy &&
      !update.isPending &&
      (!dirty || window.confirm("Discard unsaved metadata changes?"))
    )
      onClose();
  };
  const save = async () => {
    setError("");
    setNotice("");
    try {
      const parsed = tagNames(tags);
      // The marketing tag is the inclusion contract for production photos.
      if (
        production &&
        !parsed.some((tag) => tag.toLowerCase() === "marketing")
      )
        parsed.push("marketing");
      await update.mutateAsync({
        id: media.id,
        name,
        description,
        alt_text: alt,
        tags: parsed,
      });
      const normalized = parsed.join(", ");
      setTags(normalized);
      setBaseline(JSON.stringify([name, description, alt, normalized]));
      setNotice("Metadata saved.");
      onUpdated();
    } catch (cause) {
      setError(mediaError(cause));
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete “${name}” from the marketing library?`)) return;
    setBusy(true);
    setError("");
    try {
      const result = await bulkMedia({ ids: [media.id], operation: "delete" });
      if (result.skipped.length) {
        setError(result.skipped[0].reason);
        void usage.refetch();
      } else {
        await cache.invalidateQueries({ queryKey: ["combined-media"] });
        onClose();
      }
    } catch (cause) {
      setError(mediaError(cause));
    }
    setBusy(false);
  };
  const download = async () => {
    try {
      await downloadApiBlob(
        `/api/marketing/combined-media/${media.id}/download/`,
        media.original_filename || media.name,
      );
    } catch (cause) {
      setError(mediaError(cause));
    }
  };
  return (
    <Drawer
      open
      anchor="right"
      onClose={close}
      size="lg"
      slotProps={{
        content: {
          sx: {
            width: { xs: "100vw", sm: 620 },
            p: { xs: 2, sm: 3 },
            overflowY: "auto",
          },
        },
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Typography level="h4">Asset details</Typography>
          <Button
            variant="plain"
            disabled={busy || update.isPending}
            onClick={close}
          >
            Close
          </Button>
        </Stack>
        {error && <Alert color="danger">{error}</Alert>}
        {notice && <Alert color="success">{notice}</Alert>}
        <MediaPreview media={media} controls />
        <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>
          {media.original_filename || media.name} ·{" "}
          {media.mime_type || media.file_type} ·{" "}
          {media.file_size
            ? `${(media.file_size / 1024).toFixed(1)} KB`
            : "Size unavailable"}
          {media.dimensions
            ? ` · ${media.dimensions.width} × ${media.dimensions.height}`
            : " · Dimensions unavailable"}
        </Typography>
        <Typography level="body-sm">
          {media.source_app_display} ·{" "}
          {new Date(media.uploaded_at).toLocaleString()}
        </Typography>
        {media.job_id && (
          <Link href={`/operations/jobs/${media.job_id}`}>Open source job</Link>
        )}
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(media.file);
                setNotice("File link copied.");
              } catch (cause) {
                setError(mediaError(cause));
              }
            }}
          >
            Copy file link
          </Button>
          <Button variant="outlined" onClick={download}>
            Download original
          </Button>
        </Stack>
        <FormControl>
          <FormLabel>Name</FormLabel>
          <Input
            value={name}
            disabled={production || update.isPending}
            onChange={(event) => setName(event.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Description</FormLabel>
          <Textarea
            value={description}
            minRows={3}
            disabled={update.isPending}
            onChange={(event) => setDescription(event.target.value)}
          />
        </FormControl>
        {!production && media.file_type === "image" && (
          <FormControl>
            <FormLabel>Alt text</FormLabel>
            <Input
              value={alt}
              disabled={update.isPending}
              onChange={(event) => setAlt(event.target.value)}
            />
          </FormControl>
        )}
        <FormControl>
          <FormLabel>Tags, separated by commas</FormLabel>
          <Input
            value={tags}
            disabled={update.isPending}
            onChange={(event) => setTags(event.target.value)}
          />
        </FormControl>
        {production && (
          <Typography level="body-sm">
            The marketing tag keeps this photo available here. Production names
            and originals are managed in the source job.
          </Typography>
        )}
        <Button
          loading={update.isPending}
          disabled={!dirty || !name.trim()}
          onClick={save}
        >
          Save metadata
        </Button>
        <Typography level="title-lg">Known usage</Typography>
        {usage.isPending && <Typography>Checking references…</Typography>}
        {usage.isError && (
          <Alert
            color="danger"
            endDecorator={
              <Button size="sm" onClick={() => usage.refetch()}>
                Retry
              </Button>
            }
          >
            References could not be loaded.
          </Alert>
        )}
        {usage.data && (
          <>
            <Typography level="body-sm">{usage.data.scope}</Typography>
            {usage.data.references.length ? (
              usage.data.references.map((ref, index) => (
                <Box key={`${ref.kind}-${ref.id}-${index}`}>
                  <Chip size="sm">{ref.kind}</Chip>
                  <Typography>{ref.name}</Typography>
                </Box>
              ))
            ) : (
              <Typography level="body-sm">
                No known marketing references.
              </Typography>
            )}
          </>
        )}
        {!production && (
          <Button
            color="danger"
            variant="outlined"
            disabled={!usage.data?.can_delete || dirty}
            loading={busy}
            onClick={remove}
          >
            Delete asset
          </Button>
        )}
        {!!usage.data?.references.length && (
          <Alert color="warning">
            Replace or detach these references before deleting this asset.
          </Alert>
        )}
      </Stack>
    </Drawer>
  );
}
